import type { FinanceKey } from './keys';

export const en: Record<FinanceKey, string> = {
  'panel.params': 'Parameters',
  'panel.advanced': 'Advanced',
  'panel.experiment': 'Experiment',
  'action.reset': 'Reset',
  'action.randomise': 'Randomise',
  'experiment.compound-interest.label': 'Compound interest',
  'experiment.compound-interest.info': 'Compare compound and simple interest growth over time.',
  'experiment.power-of-time.label': 'Power of time',
  'experiment.power-of-time.info':
    'See how starting early beats starting late, even with less money.',
  'experiment.savings-goal.label': 'Savings goal',
  'experiment.savings-goal.info': 'Find how much you need to save monthly to reach a target.',
  'experiment.borrowing-cost.label': 'Borrowing cost',
  'experiment.borrowing-cost.info': 'Explore the true cost of loans and credit cards.',
  'experiment.inflation.label': 'Inflation',
  'experiment.inflation.info': 'Watch purchasing power erode over time.',
  'experiment.fees-drag.label': 'Fees drag',
  'experiment.fees-drag.info': 'Discover how small annual fees compound into large losses.',
  /* compound-interest */
  'param.principal.label': 'Principal',
  'param.rate.label': 'Annual rate',
  'param.years.label': 'Years',
  'param.showSimple.label': 'Show simple interest',
  'param.frequency.label': 'Compounding frequency',
  'param.frequency.annual': 'Annual',
  'param.frequency.biannual': 'Twice a year',
  'param.frequency.quarterly': 'Quarterly',
  'param.frequency.monthly': 'Monthly',
  'param.frequency.daily': 'Daily',
  'stat.final-compound.label': 'Compound final',
  'stat.final-simple.label': 'Simple final',
  'stat.gap.label': 'Gap (compound − simple)',
  'stat.apy.label': 'Effective APY',
  'stat.nominal.label': 'Nominal rate',
  'series.compound.label': 'Compound',
  'series.simple.label': 'Simple',
  'preset.starter.label': 'Starter',
  'preset.decades.label': 'Decades',
  'preset.high-rate.label': 'High rate',
  /* power-of-time */
  'param.monthly.label': 'Monthly contribution',
  'param.horizon.label': 'Horizon (years)',
  'param.a-start.label': 'Saver A start',
  'param.a-stop.label': 'Saver A stop',
  'param.b-start.label': 'Saver B start',
  'param.b-stop.label': 'Saver B stop',
  'stat.a-contributed.label': 'A contributed',
  'stat.a-final.label': 'A final balance',
  'stat.b-contributed.label': 'B contributed',
  'stat.b-final.label': 'B final balance',
  'stat.doubling.label': 'Doubling time (yrs)',
  'stat.cost-of-waiting.label': 'Cost of waiting',
  'series.saver-a.label': 'Saver A (early)',
  'series.saver-b.label': 'Saver B (late)',
  'preset.classic-early-vs-late.label': 'Classic early vs late',
  'preset.same-total.label': 'Same total invested',
  'preset.late-bloomer.label': 'Late bloomer',
  /* savings-goal */
  'param.target.label': 'Savings target',
  'param.goalSeek.label': 'Goal-seek mode',
  'stat.total-contributed.label': 'Total contributed',
  'stat.total-growth.label': 'Total growth',
  'stat.final-balance.label': 'Final balance',
  'stat.required-monthly.label': 'Required monthly',
  'series.contributions.label': 'Contributions',
  'series.growth.label': 'Growth',
  'series.goal.label': 'Goal',
  'preset.emergency-fund.label': 'Emergency fund',
  'preset.house-deposit.label': 'House deposit',
  'preset.gap-year.label': 'Gap year',
  /* borrowing-cost */
  'param.amount.label': 'Loan amount',
  'param.apr.label': 'Annual rate (APR)',
  'param.mode.label': 'Repayment mode',
  'param.mode.amortizing': 'Amortizing loan',
  'param.mode.credit-card': 'Credit card minimum',
  'param.minRate.label': 'Minimum payment rate',
  'param.extra.label': 'Extra payment/month',
  'stat.monthly-payment.label': 'Monthly payment',
  'stat.total-paid.label': 'Total paid',
  'stat.total-interest.label': 'Total interest',
  'stat.payoff-years.label': 'Payoff time (yrs)',
  'stat.interest-saved.label': 'Interest saved',
  'stat.years-saved.label': 'Years saved',
  'series.interest.label': 'Cumulative interest',
  'series.principal.label': 'Cumulative principal',
  'series.balance.label': 'Remaining balance',
  'preset.car-loan.label': 'Car loan',
  'preset.mortgage.label': 'Mortgage',
  'preset.credit-card-trap.label': 'Credit card trap',
  /* inflation */
  'param.start-amount.label': 'Amount',
  'param.inflation.label': 'Inflation rate',
  'param.savingsRate.label': 'Savings rate',
  'param.compareSavings.label': 'Compare savings account',
  'stat.real-value.label': 'Real value after years',
  'stat.real-return.label': 'Real return',
  'stat.break-even.label': 'Break-even rate',
  'stat.losing-ground.label': 'Losing ground (real)',
  'series.nominal.label': 'Nominal (cash)',
  'series.real.label': 'Real value',
  'series.savings.label': 'Savings (nominal)',
  'series.savings-real.label': 'Savings (real)',
  'preset.cash-under-mattress.label': 'Cash under mattress',
  'preset.low-savings-rate.label': 'Low savings rate',
  'preset.beats-inflation.label': 'Beats inflation',
  /* fees-drag */
  'param.fee.label': 'Annual fee',
  'stat.final-no-fee.label': 'No-fee final',
  'stat.final-with-fee.label': 'With-fee final',
  'stat.fee-cost.label': 'Total fee cost',
  'stat.fee-cost-pct.label': 'Fee cost (% of pot)',
  'stat.fee-comp-low.label': 'Cost @ 0.2% fee',
  'stat.fee-comp-typical.label': 'Cost @ 1% fee',
  'stat.fee-comp-high.label': 'Cost @ 2% fee',
  'series.no-fee.label': 'No fee',
  'series.with-fee.label': 'With fee',
  'series.fee-gap.label': 'Fee cost gap',
  'preset.low-fee.label': 'Low fee (0.2%)',
  'preset.typical-fee.label': 'Typical fee (1%)',
  'preset.high-fee.label': 'High fee (2%)',
};
